import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Users, GitBranch, ExternalLink } from "lucide-react";

const STAGES = [
  "Discovered", "Researched", "Qualified", "Outreach Ready",
  "Contacted", "Engaged", "Demo", "Trial",
  "Activating", "Activated", "Paid", "Retained",
  "Testimonial", "Referral Partner", "Lost",
];

export function GrowthLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (stage !== "all") params.stage = stage;
      const { data } = await api.get("/growth/leads", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [stage]);
  useEffect(() => { load(); }, [load]);

  const changeStage = async (lead, newStage) => {
    try {
      await api.patch(`/growth/leads/${lead.id}`, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-7xl mx-auto" data-testid="growth-leads">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><Users className="h-7 w-7 text-indigo-400" /> Leads</h1>
          <p className="text-slate-400 text-sm mt-1">Qualified opportunities turn into leads here. Drag through the fifteen pipeline stages.</p>
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-56 bg-slate-900 border-slate-700 text-slate-200" data-testid="leads-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-slate-400 flex items-center gap-2 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      : items.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-10 text-center text-slate-400"><p className="text-sm">No leads yet. Convert opportunities from the Radar.</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((l) => (
            <Card key={l.id} className="bg-slate-900/60 border-slate-800" data-testid={`lead-row-${l.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded px-1.5 py-0.5">{l.source || "manual"}</span>
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-violet-300">{l.stage}</span>
                    {l.industry && <span className="text-[10px] text-slate-500">{l.industry}</span>}
                  </div>
                  <p className="text-white font-bold mt-1">{l.company}</p>
                  <p className="text-sm text-slate-400">{l.contact_name || "—"} · {l.contact_email || l.contact_phone || "no contact"}{l.location ? ` · ${l.location}` : ""}</p>
                  {l.source_url && <a href={l.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1 mt-1">{l.source_url.slice(0, 60)}<ExternalLink className="h-3 w-3" /></a>}
                </div>
                <div className="shrink-0 space-y-2">
                  {l.lead_score != null && <p className="text-2xl font-extrabold text-indigo-300 text-right">{Math.round(l.lead_score * 100)}</p>}
                  <Select value={l.stage} onValueChange={(v) => changeStage(l, v)}>
                    <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" data-testid={`stage-select-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function GrowthPipeline() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/growth/leads", { params: { limit: 500 } });
        setItems(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const grouped = useMemo(() => {
    const g = {}; STAGES.forEach(s => g[s] = []);
    items.forEach((l) => { if (g[l.stage]) g[l.stage].push(l); });
    return g;
  }, [items]);

  return (
    <div className="p-6 md:p-8 space-y-4 max-w-full" data-testid="growth-pipeline">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><GitBranch className="h-7 w-7 text-indigo-400" /> Pipeline</h1>
        <p className="text-slate-400 text-sm mt-1">Fifteen stages. Drag will be enabled when the leads surface stabilizes — change stage inline on the Leads page for now.</p>
      </div>
      {loading ? <div className="text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div> : (
        <div className="overflow-x-auto">
          <div className="flex gap-3 min-w-max pb-4">
            {STAGES.map((s) => (
              <div key={s} className="w-64 shrink-0 bg-slate-900/60 border border-slate-800 rounded-xl p-3" data-testid={`pipeline-col-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-slate-300">{s}</p>
                  <span className="text-xs text-slate-500">{grouped[s].length}</span>
                </div>
                <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                  {grouped[s].map((l) => (
                    <div key={l.id} className="rounded-lg bg-slate-800 border border-slate-700 p-2 text-xs">
                      <p className="font-bold text-white truncate">{l.company}</p>
                      <p className="text-slate-400 truncate">{l.contact_name || l.contact_email || "no contact"}</p>
                      {l.lead_score != null && <p className="text-indigo-300 font-extrabold mt-1">{Math.round(l.lead_score * 100)}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
